'use client';

import { useState } from 'react';
import LearnMoreModal from './learn-more-modal';
import { Smile } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { PurpleLoadingSvg } from '@/components/ui/loading';
import CloseIcon from '/public/close-2.svg';
import ErrorIcon from '/public/error.svg';
import InfoIcon from '/public/info.svg';
import { useI18n } from '@/hooks/use-i18n';

export interface ThreadActionProps {
  data: TEMPLATE.Template;
  className?: string;
  generateTemplate: TEMPLATE.GenerateFn;
  delTemplate: TEMPLATE.DelFn;
  regenerateTemplate: TEMPLATE.RegenerateFn;
  viewTemplate: TEMPLATE.ViewFn;
  closeTemplate: TEMPLATE.CloseFn;
}

const ThreadAction = ({
  data,
  className,
  generateTemplate,
  delTemplate,
  regenerateTemplate,
  viewTemplate,
  closeTemplate,
}: ThreadActionProps) => {
  const { t } = useI18n('components');
  const { status, id: templateId, message_id: messageId } = data;
  const [isModalOpen, setIsModalOpen] = useState(false);
  const handleSaveTemplate = () => {
    generateTemplate({ messageId });
  };

  const handleRetry = () => {
    regenerateTemplate({ templateId, messageId });
  };

  const handleDelete = () => {
    delTemplate({ templateId, messageId });
  };

  const handleViewTemplate = () => {
    viewTemplate({ templateId, messageId });
  };

  const handleClose = () => {
    closeTemplate({ messageId });
  };

  const learnMoreNode = (
    <div className="flex gap-1 text-[12px] text-[#00000040]">
      <InfoIcon className="w-[14px] h-[14px] mt-0.5 flex-shrink-0" />
      <span>
        {t('threadAction.templateDescription')}
        <span
          onClick={() => setIsModalOpen(true)}
          className="text-primary ml-1 cursor-pointer hover:underline"
        >
          {t('threadAction.learnMore')}
        </span>
      </span>
      <LearnMoreModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );

  if (status === 'running' || status === 'queue') {
    return (
      <div className={cn('space-y-2', className)}>
        <button
          className="h-[36px] border border-[#7171EE4D] text-[#7171EE] rounded-md flex items-center justify-center text-[14px] font-medium gap-1 px-[86px]"
          onClick={() => {
            toast.info(t('threadAction.savingTemplatePleaseWait'));
          }}
        >
          <PurpleLoadingSvg className="animate-spin text-[16px]" />
          {t('threadAction.saving')}
        </button>
        {learnMoreNode}
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div
        className={cn(
          'w-fit h-10 px-3 bg-[#F5F5F5] rounded-md flex items-center justify-between text-[#000000BF] gap-12',
          className,
        )}
      >
        <div className="flex items-center gap-2">
          <Smile size={16} />
          <span className="text-[14px] font-medium">
            {t('threadAction.templateSaved')}
            <span
              className="underline cursor-pointer"
              onClick={handleViewTemplate}
            >
              {t('threadAction.clickToView')}
            </span>
          </span>
        </div>

        <CloseIcon
          className="w-[16px] h-[16px] cursor-pointer"
          onClick={handleClose}
        />
      </div>
    );
  }

  if (status === 'failed') {
    return (
      <div
        className={cn(
          'w-fit h-10 px-3 bg-[#FF00001A] rounded-md flex items-center justify-between text-[#FF0000] gap-4',
          className,
        )}
      >
        <div className="flex items-center gap-1">
          <ErrorIcon className="w-[16px] h-[16px] text-[#FF4D4F]" />
          <span className="text-[14px] font-medium">
            {t('threadAction.templateSaveFailed')}
          </span>
        </div>
        <div className="flex items-center gap-5 text-[12px]">
          <button onClick={handleRetry} className="text-[14px] cursor-pointer">
            {t('threadAction.resave')}
          </button>
          <button onClick={handleDelete} className="text-[14px] cursor-pointer">
            {t('common:delete')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn('space-y-3', className)}>
      <button
        onClick={handleSaveTemplate}
        className="h-[36px] border border-[#7171EE4D] text-[#7171EE] rounded-md hover:bg-[#7171EE4D] transition-colors text-[14px] font-medium cursor-pointer px-[76px]"
      >
        {t('threadAction.saveAsTaskTemplate')}
      </button>
      {learnMoreNode}
    </div>
  );
};

export default ThreadAction;
